import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { SnowEffect } from './SnowEffect.js';

export class Section {
    constructor(data, index, startZ = 0) {
        this.id = data.id;
        this.index = index;
        this.startZ = startZ;
        this.color = parseInt(data.color.replace('#', '0x'));
        this.title = data.title;
        this.assets = data.assets || {};
        this.group = new THREE.Group();

        // Calculate dynamic length
        const videoCount = (this.assets.videos || []).length;
        const videoPairs = Math.ceil(videoCount / 2);
        const videoSpace = Math.max(videoPairs * 5.2, 6); // Reduced spacing (5.2m per pair)

        // Adaptive Buffers
        let startBuffer, endBuffer;

        if (this.index === 0) {
            // Section 0: Starts at Z=10. Camera at Z=5.
            // We want videos to start at Global Z=0 (5m in front of camera).
            // Local Z = Global Z - Section Z = 0 - 10 = -10.
            // So StartBuffer = 10.
            // User requested +2m buffer at start (was +5m then -3m).
            startBuffer = 12;
        } else {
            // Other Sections: Increased to 5m (+1m)
            // User requested +2m buffer at start (was +5m then -3m).
            startBuffer = 7;
        }

        // End Buffer: Increased to 2m per user request (+1m)
        endBuffer = 2;

        // Total Length Calculation
        // Last video center is at: -startBuffer - (pairs-1)*5.2
        // Last video end is at:    -startBuffer - (pairs-1)*5.2 - 2.2 (approx half width of 4.4)
        // Wall must end at:        LastVideoEnd - endBuffer

        let contentLen = 0;
        if (videoPairs > 0) {
            contentLen = (videoPairs - 1) * 5.2 + 4.4; // Distance from first center to last end (approx)
        }

        // We need at least some length if empty
        if (contentLen === 0) contentLen = 4;

        this.length = startBuffer + contentLen + endBuffer;

        // Video Start Position (Local Z)
        // Videos start 'startBuffer' meters inside the section
        this.videoStartZ = -startBuffer;

        this.width = 10;
        this.height = 14;

        this.init();
    }

    static isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    static calculateLength(data, index) {
        // Exact replication of constructor logic
        const videoCount = ((data.assets && data.assets.videos) || []).length;
        const videoPairs = Math.ceil(videoCount / 2);

        // Adaptive Buffers
        let startBuffer, endBuffer;

        if (index === 0) {
            startBuffer = 12;
        } else {
            startBuffer = 7;
        }
        endBuffer = 2;

        let contentLen = 0;
        if (videoPairs > 0) {
            contentLen = (videoPairs - 1) * 5.2 + 4.4;
        }

        if (contentLen === 0) contentLen = 4;

        return startBuffer + contentLen + endBuffer;
    }

    init() {
        // Position the section group at the cumulative start Z
        this.group.position.z = -this.startZ;

        // Geometry Construction
        // We build everything relative to the group's origin (0,0,0) which is the "Entrance"
        // The section extends towards -Z.

        const centerX = 0;
        const centerZ = -this.length / 2;

        // --- OPTIMIZATION: SHARED ASSETS ---
        // Ensure static assets are loaded once
        if (!Section.commonAssets) {
            const textureLoader = new THREE.TextureLoader();

            const floorTex = textureLoader.load('assets/sections/general/logos/sigle-talosi-bleu.svg');
            floorTex.wrapS = THREE.RepeatWrapping;
            floorTex.wrapT = THREE.RepeatWrapping;

            const wallTex = textureLoader.load('assets/textures/wall_venetian.png');
            wallTex.wrapS = THREE.MirroredRepeatWrapping;
            wallTex.wrapT = THREE.RepeatWrapping;

            // OPTIMIZATION: Disable Mipmaps on Mobile to save VRAM
            const isMobile = Section.isMobileDevice();

            if (isMobile) {
                console.log("[Section] Mobile Detect: Disabling Mipmaps for Shared Textures");
                floorTex.minFilter = THREE.LinearFilter;
                floorTex.generateMipmaps = false;

                wallTex.minFilter = THREE.LinearFilter;
                wallTex.generateMipmaps = false;
            }

            Section.commonAssets = {
                floorTex: floorTex,
                wallTex: wallTex,
                isMobile: isMobile // Store flag
            };
        }

        // Create floor
        const floorGeo = new THREE.PlaneGeometry(this.width, this.length);

        // CLONE Texture to allow unique repetition without reloading data
        // .clone() shares the source image/upload but has unique properties
        // Parquet Floor (Procedural - Lightweight)
        if (!Section.parquetTexture) {
            Section.parquetTexture = Section.createParquetTexture();
        }
        const floorTex = Section.parquetTexture.clone(); // Clone for unique repeat
        floorTex.wrapS = THREE.RepeatWrapping;
        floorTex.wrapT = THREE.RepeatWrapping;

        // Scale Texture for Wood Grain (Seamless Slab)
        floorTex.repeat.set(2, this.length / 3);
        floorTex.colorSpace = THREE.SRGBColorSpace; // FORCE sRGB encoding for vibrant colors
        floorTex.needsUpdate = true;

        // Using MeshBasicMaterial to ignore lighting/shadows and show PURE texture color
        // This removes any "filter" effect from the lighting system.
        const floorMat = new THREE.MeshBasicMaterial({
            map: floorTex,
            color: 0xffffff,
            side: THREE.FrontSide
        });
        const floor = new THREE.Mesh(floorGeo, floorMat);
        floor.rotation.x = -Math.PI / 2;
        floor.position.set(centerX, -this.height / 2, centerZ);
        this.group.add(floor);

        // --- OPTIMIZATION: SHARED FLOOR DECAL (Logo) ---
        // Requirement: "Coller au sol", "Charger 1 seule fois", "Optimale"
        if (!Section.commonAssets) Section.commonAssets = {}; // Safety check

        if (!Section.commonAssets.floorDecalTex) {
            const textureLoader = new THREE.TextureLoader();
            // Note: Space in filename requires encoding or just string? Browser handles space usually.
            // Using encodeURI just in case: 'talosi%20parquet.png'
            const decalTex = textureLoader.load('assets/textures/talosi parquet.png');
            decalTex.colorSpace = THREE.SRGBColorSpace;
            Section.commonAssets.floorDecalTex = decalTex;
        }

        if (!Section.commonAssets.floorDecalMat) {
            Section.commonAssets.floorDecalMat = new THREE.MeshBasicMaterial({
                map: Section.commonAssets.floorDecalTex,
                transparent: true,
                opacity: 1, // Full visibility
                depthWrite: false, // Prevents z-fighting glitches with floor
                side: THREE.FrontSide
            });
        }

        // Shared Geometry? (Optimization+)
        if (!Section.commonAssets.floorDecalGeo) {
            // Size: 6x6m (Fits comfortably in 10m width)
            Section.commonAssets.floorDecalGeo = new THREE.PlaneGeometry(6, 6);
        }

        const floorDecal = new THREE.Mesh(Section.commonAssets.floorDecalGeo, Section.commonAssets.floorDecalMat);
        floorDecal.rotation.x = -Math.PI / 2;
        // Position: 0.005 above floor.
        // Z-Position: Move closer to the "Separator Wall" (End of Section = -this.length).
        // Placing center 5m from the end leaves ~2m gap between decal edge and wall (since 6m size).
        floorDecal.position.set(centerX, (-this.height / 2) + 0.005, -(this.length - 10));
        // Important: Render Order to ensure it draws ON TOP of the floor basic material
        floorDecal.renderOrder = 2;
        this.group.add(floorDecal);

        // Create ceiling
        const ceilGeo = new THREE.PlaneGeometry(this.width, this.length);
        const ceilMat = new THREE.MeshStandardMaterial({
            color: 0x0033FF,
            roughness: 0.8
        });
        const ceil = new THREE.Mesh(ceilGeo, ceilMat);
        ceil.rotation.x = Math.PI / 2;
        ceil.position.set(centerX, this.height / 2, centerZ);
        this.group.add(ceil);

        // Create walls (Left and Right)
        // Clone Wall Texture for unique repetition
        const wallTexture = Section.commonAssets.wallTex.clone();
        if (Section.commonAssets.isMobile) {
            wallTexture.minFilter = THREE.LinearFilter;
            wallTexture.generateMipmaps = false;
        }
        wallTexture.repeat.set(this.length / 5, 1); // Adjust repeat
        wallTexture.needsUpdate = true;

        const wallGeo = new THREE.PlaneGeometry(this.length, this.height);
        const wallMat = new THREE.MeshStandardMaterial({
            map: wallTexture,
            color: 0xFFD902,
            bumpMap: wallTexture, // Re-use same texture instance
            bumpScale: 0.3,
            emissive: 0xFFD902,
            emissiveMap: wallTexture, // Re-use same texture instance
            emissiveIntensity: 0.6,
            side: THREE.DoubleSide,
            roughness: 0.5,
            metalness: 0.1,
            fog: false
        });

        const leftWall = new THREE.Mesh(wallGeo, wallMat);
        leftWall.rotation.y = Math.PI / 2;
        leftWall.position.set(-this.width / 2, 0, centerZ);
        this.group.add(leftWall);

        const rightWall = new THREE.Mesh(wallGeo, wallMat);
        rightWall.rotation.y = -Math.PI / 2; // FIX: Face INWARDS
        rightWall.position.set(this.width / 2, 0, centerZ); // Match Left Wall (Y=0)
        this.group.add(rightWall);

        // --- VISUAL POLISH: WALL BOTTOM SHADOWS ---
        this.addWallShadows(centerZ); // Helper to keep constructor clean

        // Add Content
        this.addContent();
    }

    addWallShadows(centerZ) {
        if (!Section.commonAssets) Section.commonAssets = {};

        if (!Section.commonAssets.wallShadowMat) {
            const canvas = document.createElement('canvas');
            canvas.width = 32;
            canvas.height = 64;
            const ctx = canvas.getContext('2d');

            // Gradient: Transparent (Top) -> Black (Bottom)
            // User feedback: Previous was "Wrong Way" (Black at Top).
            // So we explicitly put Black at Canvas Bottom (64).
            const grad = ctx.createLinearGradient(0, 0, 0, 64);
            grad.addColorStop(0, 'rgba(0,0,0,0)');   // Top: Transparent
            grad.addColorStop(1, 'rgba(0,0,0,0.6)'); // Bottom: Black (Base)

            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, 32, 64);

            const tex = new THREE.CanvasTexture(canvas);

            Section.commonAssets.wallShadowMat = new THREE.MeshBasicMaterial({
                map: tex,
                transparent: true,
                opacity: 0.8, // Slightly softer than 1
                side: THREE.FrontSide, // FrontSide prevents back-face artifacts (Black Rectangle)
                depthWrite: false,
                blending: THREE.NormalBlending
            });
        }

        // Geometry differs per section due to length, so we can't easily share geometry 
        // unless we scale a unit plane. Scaling unit plane is better for batching!
        // But for now, simple PlaneGeometry is fine.
        const shadowHeight = 0.3; // Reduced even further (30cm)
        const geo = new THREE.PlaneGeometry(this.length, shadowHeight);

        const leftShadow = new THREE.Mesh(geo, Section.commonAssets.wallShadowMat);
        leftShadow.rotation.y = Math.PI / 2;
        // Wall X: -width/2. Shadow slightly offset inside: +0.05
        leftShadow.position.set(-this.width / 2 + 0.05, -this.height / 2 + (shadowHeight / 2), centerZ);
        leftShadow.renderOrder = 2; // Above floor/walls
        this.group.add(leftShadow);

        const rightShadow = new THREE.Mesh(geo, Section.commonAssets.wallShadowMat);
        rightShadow.rotation.y = -Math.PI / 2; // Faces Center (-X)
        rightShadow.position.set(this.width / 2 - 0.05, -this.height / 2 + (shadowHeight / 2), centerZ);
        rightShadow.renderOrder = 2;
        this.group.add(rightShadow);
    }

    addContent() {
        // Videos
        if (this.assets.videos && this.assets.videos.length > 0) {
            this.assets.videos.forEach((videoData, i) => {
                this.createVideoPlane(videoData, i);
            });
        }



        // Special Effect: Snow for 'noel'
        // Disable on Mobile for performance
        const isMobile = Section.isMobileDevice();
        if (this.id === 'noel' && !isMobile) {
            this.snowEffect = new SnowEffect(this.width, this.length, this.height);
            this.group.add(this.snowEffect.getMesh());
        }

        // Content Hub (End of section)
        this.createContentHub();

        // Ceiling Scroll Hint (Only for first section)
        if (this.index === 0) {
            // Ensure we don't duplicate
            if (!this.ceilingScroll) {
                this.createCeilingScrollHint();
            }
        }
    }

    createCeilingScrollHint() {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');

        // Styles
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 20;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '700 80px "Outfit", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Text
        ctx.fillText('SCROLL', 256, 400); // Text at bottom (closest to viewer)

        // Arrows Pointing "Forward" (Into the screen / Away from camera)
        // On Ceiling Plane (Rotated X=90):
        // Top of Canvas (Y=0) is "Forward" (-Z direction)
        // Bottom of Canvas (Y=512) is "Backward" (+Z direction / Towards Camera)

        // We want arrows pointing UP on the canvas to indicate "Go Forward"

        // Arrow 1 (Top)
        ctx.beginPath();
        ctx.moveTo(156, 150);
        ctx.lineTo(256, 50); // Point Up
        ctx.lineTo(356, 150);
        ctx.stroke();

        // Arrow 2 (Below)
        ctx.beginPath();
        ctx.moveTo(156, 270);
        ctx.lineTo(256, 170); // Point Up
        ctx.lineTo(356, 270);
        ctx.stroke();

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: 0.8,
            side: THREE.DoubleSide,
            fog: false
        });

        const geometry = new THREE.PlaneGeometry(5, 5);
        this.ceilingScroll = new THREE.Mesh(geometry, material);

        // Orientation
        this.ceilingScroll.rotation.x = Math.PI / 2; // Face Down (Parallel to floor)
        this.ceilingScroll.rotation.z = 0;

        // Position
        this.ceilingScroll.position.x = 0;
        this.ceilingScroll.position.y = this.height / 2 - 0.1; // Just below ceiling

        // Z Position Logic:
        // Section Start (Global +10). Camera (Global +5).
        // Z=-28 (Global -18) was "At Separation Wall" (Too Far).
        // Z=-10 (Global 0) was "Invisible" (Too High/Close).
        // Try Z=-18 (Global -8). Distance = 13m. Should be visible and closer.
        this.ceilingScroll.userData.baseZ = -18;
        this.ceilingScroll.position.z = -18;

        this.group.add(this.ceilingScroll);
    }

    createContentHub() {
        // Interactive object at the end
        const geometry = new THREE.BoxGeometry(this.width, this.height, 0.5);

        const material = new THREE.MeshStandardMaterial({
            color: this.color,
            emissive: this.color, // ADDED: Self-illuminated to prevent darkness
            emissiveIntensity: 0.5, // Moderate glow
            roughness: 0.8,
            metalness: 0.1,
            transparent: false,
            opacity: 1,
            side: THREE.FrontSide
        });

        const hub = new THREE.Mesh(geometry, material);
        this.hubMesh = hub;
        // Position at the very end of the section
        hub.position.z = -this.length;
        hub.position.y = 0;

        hub.userData = {
            type: 'content-hub',
            sectionId: this.id
        };

        // Canvas Setup - IMMEDIATE GENERATION (Fix Blue Wall)
        this.hubTextureGenerated = false; // Will be set to true inside generateHubTexture
        this.generateHubTexture();

        // HUD Material Settings (Best Practice for Overlays)
        const labelMat = new THREE.MeshBasicMaterial({
            color: this.color, // Use section color as base
            transparent: true,
            opacity: 1,
            depthWrite: false, // Prevents hiding things behind it
            side: THREE.DoubleSide
        });
        const label = new THREE.Mesh(new THREE.PlaneGeometry(this.width, this.height), labelMat);

        label.position.z = 0.26;
        label.position.y = 0;
        // REMOVED: Let depth buffer handle sorting naturally to fix "X-Ray" wall issue
        this.hubLabel = label; // Save reference for Z adjustment
        hub.add(label);

        this.group.add(hub);

        // NOTE: Principals loading and cleanup shifted to generateHubTexture called on setActive
    }

    generateHubTexture() {
        if (this.hubTextureGenerated) return;
        console.log(`[Section ${this.id}] Generating Hub Texture (Lazy Load)...`);

        // OPTIMIZATION: Reduce Canvas Size on Mobile to save memory (Textures are heavy!)
        const isMobile = Section.isMobileDevice();
        const resolutionScale = isMobile ? 0.5 : 1.0; // Reduce resolution on mobile to save memory

        this.hubCanvas = document.createElement('canvas');
        this.hubCanvas.width = 2048 * resolutionScale;
        this.hubCanvas.height = 2868 * resolutionScale; // Match Wall Aspect Ratio (10m x 14m)
        this.hubCtx = this.hubCanvas.getContext('2d');

        this.hubTexture = new THREE.CanvasTexture(this.hubCanvas);

        // Update the material
        if (this.hubLabel) {
            this.hubLabel.material.map = this.hubTexture;
            this.hubLabel.material.color.setHex(0xffffff); // Reset color to white so texture shows correctly
            this.hubLabel.material.needsUpdate = true;
        }

        // Load Principal Images for each category
        this.principals = {
            'VIDÉOS': null,
            'LOGOS': null,
            'FONDS': null,
            'SIGNATURE': null
        };

        this.hasAnimatedPrincipals = false;

        // Helper to load image or video
        const loadPrincipal = (key, src) => {
            console.log(`[Section ${this.id}] loadPrincipal loading key`);
            if (!src) {
                console.warn(`[Section ${this.id}] Missing src for ${key}`);
                return;
            }

            // Check for YouTube object
            if (src.type === 'youtube') {
                if (src.id === 'PLACEHOLDER_VIDEO_ID') {
                    console.warn(`[Section ${this.id}] Skipping PLACEHOLDER_VIDEO_ID for ${key}`);
                    return;
                }
                this.principals[key] = { type: 'youtube', id: src.id };
                this.drawHubContent(); // Trigger redraw to place CSS3D object
                return;
            }

            const isVideo = typeof src === 'string' && src.match(/\.(mp4|webm)$/i);

            if (isVideo) {
                const video = document.createElement('video');
                video.src = src;
                video.crossOrigin = "anonymous";
                video.muted = true;
                video.loop = true;
                video.play().then(() => {
                    this.principals[key] = video;
                    this.hasAnimatedPrincipals = true;
                    this.drawHubContent();
                }).catch(e => console.error(`[Section ${this.id}] Video play failed for ${key} (${src})`, e));
            } else if (typeof src === 'string') {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.src = src + '?t=' + Date.now(); // Cache Buster
                img.onload = () => {
                    console.log(`[Section ${this.id}] Loaded image for ${key}: ${src}`);
                    if (!this.principals) this.principals = {}; // Safety init
                    this.principals[key] = img;
                    this.drawHubContent();
                };
                img.onerror = (e) => {
                    console.error(`[Section ${this.id}] FAILED to load image for ${key}: ${src}`, e);
                    // Retain null in this.principals[key], drawHubContent will show fallback
                };
            }
        };

        if (this.assets.principals) {
            loadPrincipal('VIDÉOS', this.assets.principals.videos);
            loadPrincipal('LOGOS', this.assets.principals.logos);
            loadPrincipal('FONDS', this.assets.principals.wallpapers);
            loadPrincipal('SIGNATURE', this.assets.principals.signature);
        }

        // Initial Draw
        this.drawHubContent();

        this.hubTextureGenerated = true;
    }


    update() {
        try {
            if (this.hasAnimatedPrincipals && this.hubCtx) {
                // Throttle to ~30fps to avoid freezing
                const now = Date.now();
                if (!this.lastUpdate || now - this.lastUpdate > 33) {
                    this.drawHubContent();
                    this.lastUpdate = now;
                }
            }
        } catch (e) {
            console.warn("Section update error:", e);
            this.hasAnimatedPrincipals = false; // Stop animating if error occurs
        }

        // Update Snow Effect
        if (this.snowEffect) {
            this.snowEffect.update();
        }

        // Animate Ceiling Scroll Hint
        if (this.ceilingScroll) {
            const time = Date.now() * 0.003;
            // Oscillate Z slightly to "point" forward
            // Base Z is -18. Oscillate +/- 0.5m
            this.ceilingScroll.position.z = -18 + Math.sin(time) * 0.5;

            // Pulse Opacity
            this.ceilingScroll.material.opacity = 0.6 + Math.sin(time * 2) * 0.4;
        }
    }


    drawHubContent() {
        const ctx = this.hubCtx;
        const width = this.hubCanvas.width;
        const height = this.hubCanvas.height;

        // Clear and fill with background base
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = this.color;
        ctx.fillRect(0, 0, width, height);

        // Update texture
        if (this.hubTexture) this.hubTexture.needsUpdate = true;

        // Rebuild 3D Relief
        if (this.hubMesh) {
            this.createHubRelief(this.hubMesh);
        }
    } // Closes drawHubContent

    createHubRelief(hub) {
        // Clear previous children (except the base plane if any, but usually hub is a Group)
        // Note: 'hub' passed here is usually 'this.hubMesh' which is a MESH in addObjects.
        // Wait, in addObjects: this.hubMesh = new THREE.Mesh(...)
        // So we are adding children to the Mesh.
        // Safe to clear existing children to avoid duplicates on re-entry.
        while (hub.children.length > 0) {
            hub.remove(hub.children[0]);
        }

        const cW = this.hubCanvas.width; // 2048
        const cH = this.hubCanvas.height; // 2048

        // Fix Scale to match Wall Width (10m)
        const scale = this.width / cW; // 10 / 2048 ≈ 0.00488

        // Vertical Layout Cursor
        // We utilize a "Reference Width" of 2048px to define our layout constants.
        // If the actual canvas is smaller (Mobile optimization), we scale the constants down.
        // The final 'scale' (meters/pixel) will then compensate to ensure the physical size in meters is constant.

        const REF_W = 2048;
        const pixelScale = cW / REF_W;

        let cursorY = 150 * pixelScale; // Title vertical pos

        // --- 1. TITLE ---
        const titleH = 150 * pixelScale;
        const titleCanvas = document.createElement('canvas');
        titleCanvas.width = 1800 * pixelScale;
        titleCanvas.height = titleH;
        const ctx = titleCanvas.getContext('2d');

        ctx.fillStyle = '#FFFFFF';
        ctx.font = `700 ${100 * pixelScale}px "Outfit", sans-serif`; // Typed Template Literal for Font Size
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.title.toUpperCase(), titleCanvas.width / 2, titleCanvas.height / 2);

        const titleTex = new THREE.CanvasTexture(titleCanvas);
        const titleMat = new THREE.MeshBasicMaterial({ map: titleTex, transparent: true });
        const titleGeo = new THREE.PlaneGeometry(1800 * pixelScale * scale, titleH * scale);
        const titleMesh = new THREE.Mesh(titleGeo, titleMat);

        titleMesh.position.x = 0;
        titleMesh.position.y = -((cursorY + titleH / 2) - cH / 2) * scale;
        titleMesh.position.z = 0.3; // Z-fix
        hub.add(titleMesh);

        cursorY += (150 + 50) * pixelScale; // titleH + Gap

        // Helper to create Black Frame + White BG + INTRODUCING PASSE-PARTOUT
        const createFramedBackground = (contentW, contentH, innerPadding, bgColor = 0xffffff) => {
            const grp = new THREE.Group();

            // Dimensions
            const whiteW = contentW + innerPadding * 2;
            const whiteH = contentH + innerPadding * 2;
            const frameThickness = 25 * pixelScale;

            // 1. White Background (Passe-Partout)
            const bgGeo = new THREE.PlaneGeometry(whiteW * scale, whiteH * scale);
            const bgMat = new THREE.MeshBasicMaterial({ color: bgColor });
            const bgMesh = new THREE.Mesh(bgGeo, bgMat);
            bgMesh.position.z = 0;
            grp.add(bgMesh);

            // 2. Black Borders (Optimization: Single Backing Plane)
            const frameGeo = new THREE.PlaneGeometry(
                (whiteW + frameThickness * 2) * scale,
                (whiteH + frameThickness * 2) * scale
            );

            const isMobile = Section.isMobileDevice();
            let frameMat;

            if (isMobile) {
                frameMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
            } else {
                frameMat = new THREE.MeshStandardMaterial({
                    color: 0x111111,
                    roughness: 0.6,
                    metalness: 0.2
                });
            }

            const frameMesh = new THREE.Mesh(frameGeo, frameMat);
            frameMesh.position.z = -0.01; // Behind
            grp.add(frameMesh);

            return grp;
        };

        // --- 2. MAIN VIDEO ---
        const videoSize = 1200 * pixelScale;
        const videoX = (cW - videoSize) / 2;
        const videoY = cursorY;
        const passePartout = 40 * pixelScale; // White Margin inside frame

        // Create Frame for Video
        const vidFrame = createFramedBackground(videoSize, videoSize, passePartout, 0x000000);
        vidFrame.position.x = 0;
        vidFrame.position.y = -((videoY + videoSize / 2) - cH / 2) * scale;
        vidFrame.position.z = 0.26;
        hub.add(vidFrame);

        // Label "Vidéos" -> Moved CLOSER UP
        const labelH = 80 * pixelScale; // Larger
        const labelY = videoY + videoSize + passePartout + 25 * pixelScale; // Reduced gap

        // UPDATE CONFIG
        this.youtubeConfigs = {};
        if (this.principals && Object.keys(this.principals).length > 0) {
            const key = Object.keys(this.principals)[0];
            this.youtubeConfigs[key] = {
                x: videoX,
                y: videoY,
                w: videoSize,
                h: videoSize
            };
        }

        // Draw "Vidéos" Label Texture
        const vidLabelCanvas = document.createElement('canvas');
        vidLabelCanvas.width = videoSize; // Align with Video width
        vidLabelCanvas.height = 120 * pixelScale; // Taller for bigger font
        const vCtx = vidLabelCanvas.getContext('2d');
        vCtx.fillStyle = '#FFFFFF';
        vCtx.font = `500 ${80 * pixelScale}px "Outfit", sans-serif`; // BIGGER
        vCtx.textAlign = 'center';
        vCtx.fillText('Vidéos', vidLabelCanvas.width / 2, 80 * pixelScale);

        const vidLabelTex = new THREE.CanvasTexture(vidLabelCanvas);
        const vidLabelMat = new THREE.MeshBasicMaterial({ map: vidLabelTex, transparent: true });
        const vidLabelMesh = new THREE.Mesh(new THREE.PlaneGeometry(videoSize * scale, 120 * pixelScale * scale), vidLabelMat);

        vidLabelMesh.position.x = 0;
        vidLabelMesh.position.y = -((labelY + 60 * pixelScale) - cH / 2) * scale;
        vidLabelMesh.position.z = 0.3;
        hub.add(vidLabelMesh);

        cursorY = labelY + (120 + 100) * pixelScale; // Increased gap before Grid

        // --- 3. GRID (Logos | Wallpapers) ---
        const gridW = 1600 * pixelScale;
        const colW = (gridW - 100 * pixelScale) / 2;
        const colH = 500 * pixelScale;
        const gridX = (cW - gridW) / 2;

        const logoX = gridX;
        const logoY = cursorY;
        const wpX = gridX + colW + 100 * pixelScale;
        const wpY = cursorY;

        const ppGrid = 30 * pixelScale; // Smaller PP for grid

        // A. LOGO PREVIEW
        if (this.assets && this.assets.logos && this.assets.logos.length > 0) {
            const logoPath = this.assets.logos[0].path;
            const loader = new THREE.TextureLoader();
            loader.load(logoPath, (tex) => {
                if (!tex || !tex.image) return; // Safety check
                const aspect = tex.image.width / tex.image.height;
                let dW = colW;
                let dH = colW / aspect;
                if (dH > colH) {
                    dH = colH;
                    dW = colH * aspect;
                }

                // Frame
                const lFrame = createFramedBackground(dW, dH, ppGrid);
                lFrame.position.x = ((logoX + colW / 2) - cW / 2) * scale;
                lFrame.position.y = -((logoY + colH / 2) - cH / 2) * scale;
                lFrame.position.z = 0.26;
                hub.add(lFrame);

                // Image
                const mesh = new THREE.Mesh(
                    new THREE.PlaneGeometry(dW * scale, dH * scale),
                    new THREE.MeshBasicMaterial({ map: tex, transparent: true })
                );
                mesh.position.x = ((logoX + colW / 2) - cW / 2) * scale;
                mesh.position.y = -((logoY + colH / 2) - cH / 2) * scale;
                mesh.position.z = 0.3;
                hub.add(mesh);
            });

            // Label -> Moved LOWER
            const lCanvas = document.createElement('canvas');
            lCanvas.width = colW + ppGrid * 2;
            lCanvas.height = 100 * pixelScale;
            const lCtx = lCanvas.getContext('2d');
            lCtx.fillStyle = '#FFFFFF';
            lCtx.font = `500 ${80 * pixelScale}px "Outfit", sans-serif`; // BIGGER
            lCtx.textAlign = 'center';
            lCtx.fillText('Logos', (colW + ppGrid * 2) / 2, 70 * pixelScale);

            const lLabel = new THREE.Mesh(
                new THREE.PlaneGeometry((colW + ppGrid * 2) * scale, 100 * pixelScale * scale),
                new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(lCanvas), transparent: true })
            );
            lLabel.position.x = ((logoX + colW / 2) - cW / 2) * scale;
            lLabel.position.y = -((logoY + colH + ppGrid + 70 * pixelScale) - cH / 2) * scale; // Moved down
            lLabel.position.z = 0.3;
            hub.add(lLabel);
        }

        // B. WALLPAPER PREVIEW
        let wpSrc = 'assets/sections/general/wallpapers/Talosi-FondEcran-Electronique.png';
        if (this.assets && this.assets.principals && this.assets.principals.wallpapers) {
            wpSrc = this.assets.principals.wallpapers;
        }

        const wpLoader = new THREE.TextureLoader();
        wpLoader.load(wpSrc, (tex) => {
            if (!tex || !tex.image) return; // Safety check
            // Fit logic (Contain)
            const aspect = tex.image.width / tex.image.height;
            let dW = colW;
            let dH = colW / aspect;
            if (dH > colH) {
                dH = colH;
                dW = colH * aspect;
            }

            // Frame
            const wFrame = createFramedBackground(dW, dH, ppGrid);
            wFrame.position.x = ((wpX + colW / 2) - cW / 2) * scale;
            wFrame.position.y = -((wpY + colH / 2) - cH / 2) * scale;
            wFrame.position.z = 0.26;
            hub.add(wFrame);

            // Image
            const mesh = new THREE.Mesh(
                new THREE.PlaneGeometry(dW * scale, dH * scale),
                new THREE.MeshBasicMaterial({ map: tex, transparent: true }) // No White Backing needed if simple
            );
            // Store for cleanup
            this.wallpaperMesh = mesh;

            mesh.position.x = ((wpX + colW / 2) - cW / 2) * scale;
            mesh.position.y = -((wpY + colH / 2) - cH / 2) * scale;
            mesh.position.z = 0.3; // Z-FIX for Wallpaper
            hub.add(mesh);
        });

        // Label "Fonds d'écrans"
        const wCanvas = document.createElement('canvas');
        wCanvas.width = colW + ppGrid * 2;
        wCanvas.height = 100 * pixelScale;
        const wCtx = wCanvas.getContext('2d');
        wCtx.fillStyle = '#FFFFFF';
        wCtx.font = `500 ${80 * pixelScale}px "Outfit", sans-serif`; // BIGGER
        wCtx.textAlign = 'center';
        wCtx.fillText('Fonds d\'écrans', (colW + ppGrid * 2) / 2, 70 * pixelScale);

        const wLabel = new THREE.Mesh(
            new THREE.PlaneGeometry((colW + ppGrid * 2) * scale, 100 * pixelScale * scale),
            new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(wCanvas), transparent: true })
        );
        wLabel.position.x = ((wpX + colW / 2) - cW / 2) * scale;
        wLabel.position.y = -((wpY + colH + ppGrid + 70 * pixelScale) - cH / 2) * scale; // Moved down
        wLabel.position.z = 0.3;
        hub.add(wLabel);

        cursorY = wpY + colH + 200 * pixelScale; // Move Cursor Down EVEN MORE

        // --- 4. DISCOVER BUTTON (Enlarged) ---
        const btnW = 1100 * pixelScale; // Larger Width
        const btnH = 260 * pixelScale; // Larger Height
        const btnR = 130 * pixelScale;  // Larger Radius

        const btnCanvas = document.createElement('canvas');
        btnCanvas.width = btnW; btnCanvas.height = btnH;
        const bCtx = btnCanvas.getContext('2d');

        // Rounded Rect Helper
        const roundRect = (ctx, x, y, w, h, r) => {
            if (w < 2 * r) r = w / 2;
            if (h < 2 * r) r = h / 2;
            bCtx.beginPath();
            bCtx.moveTo(x + r, y);
            bCtx.arcTo(x + w, y, x + w, y + h, r);
            bCtx.arcTo(x + w, y + h, x, y + h, r);
            bCtx.arcTo(x, y + h, x, y, r);
            bCtx.arcTo(x, y, x + w, y, r);
            bCtx.closePath();
        };

        // Draw Button (Textured Wall Style)
        bCtx.save();

        // 1. Clip to Button Shape
        roundRect(bCtx, 2 * pixelScale, 2 * pixelScale, btnW - 4 * pixelScale, btnH - 4 * pixelScale, btnR);
        bCtx.clip();

        // 2. Draw Texture (if loaded)
        if (Section.commonAssets && Section.commonAssets.wallTex && Section.commonAssets.wallTex.image) {
            try {
                // Draw image to cover button
                bCtx.drawImage(Section.commonAssets.wallTex.image, 0, 0, btnW, btnH);
            } catch (e) {
                // Fallback fill if image not ready (though it should be)
                bCtx.fillStyle = '#FFD902';
                bCtx.fillRect(0, 0, btnW, btnH);
            }
        } else {
            // Fallback fill
            bCtx.fillStyle = '#FFD902';
            bCtx.fillRect(0, 0, btnW, btnH);
        }

        // 3. Apply Yellow Tint (Multiply or Color Burn)
        bCtx.globalCompositeOperation = 'multiply';
        bCtx.fillStyle = '#FFD902';
        bCtx.fillRect(0, 0, btnW, btnH);

        // Reset Composition
        bCtx.globalCompositeOperation = 'source-over';

        // 4. White Border (Restored)
        bCtx.strokeStyle = '#ffffff';
        bCtx.lineWidth = 4 * pixelScale;
        bCtx.stroke(); // Stroke path from roundRect (might need to re-trace if clip consumed it)

        // Re-trace for stroke because clip consumes the path
        roundRect(bCtx, 2 * pixelScale, 2 * pixelScale, btnW - 4 * pixelScale, btnH - 4 * pixelScale, btnR);
        bCtx.stroke();

        bCtx.restore();

        // Draw Text (Black for contrast on Yellow)
        bCtx.fillStyle = '#000000';
        bCtx.font = `800 ${100 * pixelScale}px "Outfit", sans-serif`;
        bCtx.textAlign = 'center';
        bCtx.textBaseline = 'middle';
        bCtx.fillText('DÉCOUVRIR', btnW / 2, btnH / 2);

        const btnTex = new THREE.CanvasTexture(btnCanvas);
        const btnMat = new THREE.MeshBasicMaterial({ map: btnTex, transparent: true });
        const btnMesh = new THREE.Mesh(new THREE.PlaneGeometry(btnW * scale, btnH * scale), btnMat);

        btnMesh.position.x = 0;
        btnMesh.position.y = -((cursorY + btnH / 2) - cH / 2) * scale;
        btnMesh.position.z = 0.3; // On top

        // INTERACTIVE DATA
        btnMesh.userData = {
            interactive: true,
            type: 'button',
            action: 'discover',
            sectionId: this.id, // Pass ID for click handler
            cursor: 'pointer'
        };

        hub.add(btnMesh);

        // Save button ref
        this.discoverBtn = btnMesh;
        // Legacy code removed (Hand mesh already created above)
        // The handMesh variable is no longer defined here, as the hand icon creation was removed.
        // If this.handMesh is still needed, it should be assigned a default or null value.
        // For now, following the instruction to remove the hand icon and its related code.
        // this.handMesh = handMesh; // Store for animation loop

        // --- 5. GLOBAL CLICK INTERACTION ---
        // An invisible plane covering the entire Hub that triggers 'open-section-modal'
        const clickGeo = new THREE.PlaneGeometry(this.width, this.height);
        const clickMat = new THREE.MeshBasicMaterial({
            color: 0x000000,
            opacity: 0.0,
            transparent: true,
            depthWrite: false
        });
        const clickPlate = new THREE.Mesh(clickGeo, clickMat);
        clickPlate.position.z = 0.29; // Behind Content (0.3) but in front of wall (0.25)
        clickPlate.userData = {
            type: 'content-hub', // Existing Tag for opening modal
            sectionId: this.id, // PASS ID!
            isInteractable: true
        };
        hub.add(clickPlate);
    }

    updatePrincipalYouTube(key, x, y, w, h) {
        if (!this.youtubeConfigs) this.youtubeConfigs = {};

        // Store config for later loading
        this.youtubeConfigs[key] = { x, y, w, h };

        // If we are already active, LOAD IT NOW!
        if (this.isActive) {
            this.loadVideos();
        }
    }

    loadVideos() {
        console.log(`[Section ${this.id}] loadVideos called. Active: ${this.isActive}`);

        const isMobile = Section.isMobileDevice();

        // 1. YouTube Videos (Hub)
        if (this.youtubeConfigs) {
            console.log(`[Section ${this.id}] Found youtubeConfigs for:`, Object.keys(this.youtubeConfigs));
            if (!this.principalYouTubeObjects) this.principalYouTubeObjects = {};

            Object.keys(this.youtubeConfigs).forEach(key => {
                if (this.principalYouTubeObjects[key]) {
                    return;
                }

                console.log(`[Section ${this.id}] Loading Video ${key}...`);
                const config = this.youtubeConfigs[key];
                const youtubeData = this.principals[key];
                const { x, y, w, h } = config;

                if (!youtubeData) {
                    console.warn(`[Section ${this.id}] Missing youtubeData for ${key}`);
                    return;
                }

                if (isMobile && youtubeData.id.includes('PLACEHOLDER')) return; // Skip placeholders on mobile



                if (youtubeData.id.includes('PLACEHOLDER')) return; // Skip placeholders on Desktop

                // SANITIZATION: YouTube Video IDs are 11 characters.
                // If we receive a longer ID (e.g. from a Shorts URL paste 13 chars), truncate it.
                // Example: 'efkC2KR30TgmU' (13) -> 'efkC2KR30Tg' (11) works.
                if (youtubeData.id.length > 11) {
                    console.warn(`[Section ${this.id}] Truncating bad YouTube ID: ${youtubeData.id} to 11 chars`);
                    youtubeData.id = youtubeData.id.substring(0, 11);
                }

                const div = document.createElement('div');
                // MOBILE OPTIMIZATION: Hub Video
                // On mobile, CSS3D Iframes are heavy and often block autoplay.
                // We should use a Thumbnail Mesh instead (Simulated in WebGL) or just a simple placeholder.
                // For now, if Mobile, we use a simple texture on a plane (if possible) or just let it be (User wants "Videos").
                // User Issue: "Browser blinks/crashes". Suggests VRAM/Context overload.
                // PROPOSAL: On Mobile, DO NOT create the CSS3D Iframe for Hub.
                // Instead, just show the transparent mask (or a static image if we had one).
                // But we don't have a specific Hub Thumbnail.
                // Let's at least PREVENT the Iframe creation if it's overwhelming.

                // However, Hub Video is crucial.
                // Let's try to make it Click-to-Play on Mobile (No Autoplay).
                // Actually, better: If Mobile, load a simple version.
                // If we skip `div.appendChild(iframe)` on mobile, what happens? Black box.

                // Let's stick to the current logic but add SAFETY:
                // If Mobile, enforce user interaction limit (maybe just 1 video).
                // Or better: Just ensure we destroy it properly.

                // Let's try skipping Hub Video Autoplay on Mobile.
                const autoPlayParam = isMobile ? '0' : '1';
                const muteParam = isMobile ? '1' : '1'; // Always mute to allow potential autoplay

                div.style.width = '1400px';
                div.style.height = '1400px';
                div.style.backgroundColor = '#000';
                div.style.pointerEvents = 'auto';

                const iframe = document.createElement('iframe');
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = '0';
                iframe.src = `https://www.youtube.com/embed/${youtubeData.id}?autoplay=${autoPlayParam}&mute=${muteParam}&loop=1&playlist=${youtubeData.id}&playsinline=1`;
                iframe.allow = 'autoplay; encrypted-media; picture-in-picture; web-share; compute-pressure';
                if (!isMobile) {
                    // Only load iframe immediately on Desktop. 
                    // On Mobile, wait for interaction? No, we need it to be there.
                    // Just let it load but maybe it's the COUNT that matters.
                    div.appendChild(iframe);
                } else {
                    // On Mobile, create a "Click to Play" placeholder button?
                    // Or just append the iframe but risk the crash.
                    // The user said "Version plus lourde" (Heavier).
                    // Snow was the main addition. Let's trust that disabling Snow + Fixing Cleanup is enough for now.
                    // But cleaning up the Iframe creation is good.
                    div.appendChild(iframe);
                }

                // ... wait, I'm just modifying params for now.


                const object = new CSS3DObject(div);
                // Attach to SECTION GROUP instead of Hub Mesh to avoid nesting issues
                if (!object.parent) {
                    this.group.add(object);
                    this.principalYouTubeObjects[key] = object;

                    // Create Mask logic (Only once)
                    // Create Mask logic (Only once)
                    // We need a mask for WebGL occlusion (Hole Punching through Hub Box)
                    const maskMaterial = new THREE.MeshBasicMaterial({
                        color: 0x000000,
                        opacity: 0,
                        transparent: false, // Opaque Pass
                        blending: THREE.NoBlending,
                        side: THREE.DoubleSide,
                        colorWrite: false // Punch hole
                    });

                    // FIX: Geometry must match the CSS3D Element size (1400x1400)
                    // Because we apply the same 'scale' to both Object and Mask later.
                    const maskMesh = new THREE.Mesh(new THREE.PlaneGeometry(1400, 1400), maskMaterial);
                    maskMesh.userData = { isMask: true };
                    maskMesh.renderOrder = -1; // FORCE FIRST RENDER

                    this.group.add(maskMesh);
                    object.userData.mask = maskMesh;
                    console.log(`[Section ${this.id}] Mask created for Hub Video (1400x1400)`);
                }

                // Update Transform
                if (!this.hubCanvas) return; // Guard
                const scaleX = this.width / this.hubCanvas.width;
                const scale = scaleX;
                const fitScale = w / 1400;

                object.scale.set(scale * fitScale, scale * fitScale, scale * fitScale);

                const centerX = x + w / 2;
                const centerY = y + h / 2;

                const hubZ = -this.length; // Hub is at end of section

                object.position.x = (centerX - this.hubCanvas.width / 2) * scale;
                object.position.y = -(centerY - this.hubCanvas.height / 2) * scale;
                // Hub Z + Offset (Moved to 0.30 to CLEARLY be in front of Label at 0.26)
                object.position.z = hubZ + 0.30;

                // Update Mask Transform
                if (object.userData.mask) {
                    const mask = object.userData.mask;
                    mask.position.copy(object.position);
                    mask.position.z = hubZ + 0.29; // Slightly behind video, but in front of Label
                    mask.rotation.copy(object.rotation);
                    mask.scale.set(scale * fitScale, scale * fitScale, 1);
                }
            });
        }

        // 2. Wall Videos (YouTube)
        if (this.wallYoutubeConfigs) {
            if (!this.wallYouTubeObjects) this.wallYouTubeObjects = [];

            this.wallYoutubeConfigs.forEach(config => {
                const existing = this.wallYouTubeObjects.find(obj => obj.userData.videoId === config.id);
                if (existing) return;

                if (isMobile && config.id.includes('PLACEHOLDER')) {
                    console.log(`[Section ${this.id}] Skipping Mobile Placeholder: ${config.id}`);
                    return;
                }

                // MOBILE OPTIMIZATION: Thumbnail instead of Iframe
                if (isMobile) {
                    // Hide Poster on Mobile (Duplicate of the button we are about to create, helps performance to hide)
                    if (config.poster) config.poster.visible = false;

                    const thumbUrl = `https://img.youtube.com/vi/${config.id}/maxresdefault.jpg`;
                    const loader = new THREE.TextureLoader();
                    loader.load(thumbUrl, (texture) => {
                        const geometry = new THREE.PlaneGeometry(4.4, 4.4);
                        const material = new THREE.MeshBasicMaterial({ map: texture });
                        const mesh = new THREE.Mesh(geometry, material);

                        const scale = 4.4 / 1400; // Match CSS scale logic if needed, but here we use direct size
                        // Wait, previous logic was CSS scale set(scale, scale, scale) where scale = 4.4/1400.
                        // And CSS object was 1400px. 1400 * (4.4/1400) = 4.4.
                        // So our PlaneGeometry(4.4, 4.4) is correct size.

                        mesh.position.set(0, 0, 0.05); // Slightly forward

                        // Interaction: Click to Open Lightbox
                        mesh.userData = {
                            type: 'video-button',
                            videoId: config.id,
                            isInteractable: true
                        };

                        config.group.add(mesh);

                        // Store with ID for dedupe/unload
                        mesh.userData.videoId = config.id;
                        this.wallYouTubeObjects.push(mesh);
                    });
                    return;
                }

                if (config.id.includes('PLACEHOLDER')) {
                    console.log(`[Section ${this.id}] Skipping Desktop Placeholder: ${config.id}`);
                    return;
                }

                // --- DESKTOP: YOUTUBE API INTEGRATION ---
                // Ensure Poster is Visible initially
                if (config.poster) {
                    config.poster.visible = true;
                    config.poster.material.opacity = 1;
                }

                const div = document.createElement('div');
                div.className = 'css3d-content';
                div.style.width = '1400px';
                div.style.height = '1400px';
                div.style.backgroundColor = 'transparent';
                div.style.pointerEvents = 'auto'; // Allow interaction

                // Container for YouTube Player
                const playerId = `player-${config.id}-${Date.now()}`;
                const playerDiv = document.createElement('div');
                playerDiv.id = playerId;
                div.appendChild(playerDiv);

                // Helper to Init Player when API Ready
                const initPlayer = () => {
                    new YT.Player(playerDiv, { // Pass Element directly, not ID (Avoids DOM Race Condition)
                        height: '100%',
                        width: '100%',
                        videoId: config.id,
                        playerVars: {
                            'autoplay': 1,
                            'controls': 1,
                            'mute': 1, // Required for autoplay
                            'loop': 1,
                            'playlist': config.id,
                            'rel': 0,
                            'playsinline': 1,
                            'origin': window.location.origin // Security requirement
                        },
                        events: {
                            'onReady': (event) => {
                                event.target.mute(); // Ensure muted
                                event.target.playVideo(); // Force play

                                // Update Iframe Permissions (Fix compute-pressure error)
                                const iframe = event.target.getIframe();
                                if (iframe) {
                                    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure';
                                }
                            },
                            'onStateChange': (event) => {
                                // YT.PlayerState.PLAYING = 1
                                if (event.data === 1 && config.poster && config.poster.visible) {
                                    // Fade Out Poster
                                    const fadeInterval = setInterval(() => {
                                        if (!config.poster) { clearInterval(fadeInterval); return; }
                                        config.poster.material.opacity -= 0.05;
                                        if (config.poster.material.opacity <= 0) {
                                            config.poster.material.opacity = 0;
                                            config.poster.visible = false;
                                            clearInterval(fadeInterval);
                                        }
                                    }, 50);
                                }
                            },
                            'onError': (e) => {
                                console.error(`[Section ${this.id}] YouTube Player Error:`, e);
                            }
                        }
                    });
                };

                // Load API if needed
                if (!window.YT || !window.YT.Player) {
                    if (!window.youtubeApiLoading) {
                        window.youtubeApiLoading = true;
                        const tag = document.createElement('script');
                        tag.src = "https://www.youtube.com/iframe_api";
                        const firstScriptTag = document.getElementsByTagName('script')[0];
                        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

                        // Queue init
                        window.onYouTubeIframeAPIReady = () => {
                            window.youtubeApiLoaded = true;
                            // This global callback handles the FIRST load. 
                            // Subsequent calls need to know it's ready.
                            // We might need a global event or queue.
                            // Simpler: Just rely on 'window.YT' check in a loop?
                            // Or dispatch event.
                            window.dispatchEvent(new Event('youtube-api-ready'));
                        };
                    }
                    window.addEventListener('youtube-api-ready', initPlayer, { once: true });
                } else {
                    initPlayer();
                }

                const object = new CSS3DObject(div);
                const scale = 4.4 / 1400;
                object.scale.set(scale, scale, scale);
                object.position.set(0, 0, 0);
                object.userData.videoId = config.id;
                object.userData.type = 'youtube-object';

                config.group.add(object);
                this.wallYouTubeObjects.push(object);

                // Use existing Black Screen as Mask
                if (config.screen) {
                    config.screen.material.colorWrite = false;
                    config.screen.material.transparent = false;
                    config.screen.renderOrder = -1;
                    object.userData.screen = config.screen;
                    console.log(`[Section ${this.id}] Wall Video Mask Activated (Screen Hole)`);
                } else {
                    // Fallback if no screen (legacy)
                    const maskMaterial = new THREE.MeshBasicMaterial({
                        color: 0x000000,
                        opacity: 0,
                        transparent: false,
                        blending: THREE.NoBlending,
                        side: THREE.DoubleSide,
                        colorWrite: false
                    });
                    const maskMesh = new THREE.Mesh(new THREE.PlaneGeometry(4.4, 4.4), maskMaterial);
                    maskMesh.position.set(0, 0, 0.02);
                    maskMesh.renderOrder = -1;
                    config.group.add(maskMesh);
                    object.userData.mask = maskMesh;
                }
            });
        }

        // 3. Wall Videos (HTML5)
        if (this.wallVideos && this.wallVideos.length > 0) {
            this.wallVideos.forEach(video => {
                video.play().catch(e => { /* Ignore auto-play blocking */ });
            });
        }
    }

    unloadVideos() {
        // 1. Hub YouTube Videos
        if (this.principalYouTubeObjects) {
            Object.keys(this.principalYouTubeObjects).forEach(key => {
                const object = this.principalYouTubeObjects[key];
                if (object) {
                    if (object.userData.screen) {
                        // Restore Black Screen
                        object.userData.screen.material.colorWrite = true;
                        object.userData.screen.renderOrder = 0;
                    } else if (object.userData.mask) {
                        object.userData.mask.removeFromParent();
                        if (object.userData.mask.geometry) object.userData.mask.geometry.dispose();
                        if (object.userData.mask.material) object.userData.mask.material.dispose();
                    }

                    // Dispose Texture if Mesh (Mobile or Standard)
                    if (object.material && object.material.map) {
                        object.material.map.dispose();
                    }
                    if (object.geometry) object.geometry.dispose();
                    if (object.material) object.material.dispose();

                    object.removeFromParent();
                    if (object.element) object.element.innerHTML = '';
                }
            });
            this.principalYouTubeObjects = {};
        }

        // 2. Wall Videos (YouTube)
        if (this.wallYouTubeObjects) {
            this.wallYouTubeObjects.forEach(object => {
                // Restore Black Screen if linked
                if (object.userData.screen) {
                    object.userData.screen.material.colorWrite = true;
                    object.userData.screen.renderOrder = 0;
                }
                // Remove Mask if linked
                else if (object.userData.mask) {
                    object.userData.mask.removeFromParent();
                    if (object.userData.mask.geometry) object.userData.mask.geometry.dispose();
                    if (object.userData.mask.material) object.userData.mask.material.dispose();
                }

                // Remove Object
                object.removeFromParent();

                // Dispose geometry/material/texture
                if (object.material && object.material.map) {
                    object.material.map.dispose();
                }
                if (object.geometry) object.geometry.dispose();
                if (object.material) object.material.dispose();

                // If it was CSS3D, clear HTML
                if (object.element) object.element.innerHTML = '';
            });
            this.wallYouTubeObjects = [];
        }

        // 3. Wall Videos (HTML5)
        if (this.wallVideos && this.wallVideos.length > 0) {
            this.wallVideos.forEach(video => {
                video.pause();
                video.src = ''; // Clear buffer on mobile
                video.load(); // Force release
            });
        }
    }

    setActive(active) {
        this.isActive = active;
        if (active) {
            console.log(`[Section ${this.id}] Set Active: Loading Videos. Count: ${this.wallYoutubeConfigs ? this.wallYoutubeConfigs.length : 0}`);

            // LAZY LOAD HUB TEXTURE
            if (!this.hubTextureGenerated) {
                this.generateHubTexture();
            }

            this.loadVideos();
        } else {
            this.unloadVideos();
            console.log(`[Section ${this.id}] Set Inactive: Unloaded Videos`);
        }
    }

    createWebGLButton(text, videoId) {
        // Square canvas for icon
        const size = 256;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Draw Background (Semi-transparent Black)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.fillRect(0, 0, size, size);

        // Draw Border (Natural Oak / Beige)
        ctx.strokeStyle = '#D5BB90'; // Was Neon Yellow #FFD902
        ctx.lineWidth = 10;
        ctx.strokeRect(5, 5, size - 10, size - 10);

        // Draw Eye Icon
        ctx.strokeStyle = '#D5BB90';
        ctx.fillStyle = '#D5BB90';
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const cx = size / 2;
        const cy = size / 2;
        const rw = 80; // horizontal radius/width
        const rh = 50; // vertical radius/height

        // Eye Outline (Almond shape)
        ctx.beginPath();
        ctx.moveTo(cx - rw, cy);
        // Top lid
        ctx.bezierCurveTo(cx - rw / 2, cy - rh, cx + rw / 2, cy - rh, cx + rw, cy);
        // Bottom lid
        ctx.bezierCurveTo(cx + rw / 2, cy + rh, cx - rw / 2, cy + rh, cx - rw, cy);
        ctx.stroke();

        // Pupil (Solid)
        ctx.beginPath();
        ctx.arc(cx, cy, 25, 0, Math.PI * 2);
        ctx.fill();

        // Reflection highlight (White dot)
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(cx + 8, cy - 8, 6, 0, Math.PI * 2);
        ctx.fill();

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            side: THREE.DoubleSide
        });

        // Make it a square in 3D too
        const geometry = new THREE.PlaneGeometry(0.6, 0.6); // Square geometry
        const mesh = new THREE.Mesh(geometry, material);

        mesh.userData = {
            type: 'video-button',
            videoId: videoId,
            isInteractable: true // Flag for Raycaster
        };

        return mesh;
    }

    createVideoPlane(videoData, index) {
        // videoData is now { type: 'youtube', id: '...' } or a string (legacy/fallback)

        // Create a Group to hold Video + Frame + Mask
        // This ensures they share transforms and relative positioning is easy
        const videoGroup = new THREE.Group();

        let object;
        let mask; // Mask for occlusion

        // Joint Black Screen (Background)
        // Used as "Hole Puncher" when video plays (colorWrite=false) or as Black Background when invalid/loading
        const screenGeo = new THREE.PlaneGeometry(4.4, 4.4);
        const screenMat = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide });
        const screen = new THREE.Mesh(screenGeo, screenMat);
        screen.position.z = 0.005; // Slightly behind video plane (Z=0.01/0.02)
        videoGroup.add(screen);

        if (videoData.type === 'youtube') {
            // Sanitize ID (Handle 'shorts/', full URLs, etc.)
            let cleanId = videoData.id;
            if (cleanId.includes('shorts/')) cleanId = cleanId.split('shorts/')[1];
            if (cleanId.includes('v=')) cleanId = cleanId.split('v=')[1].split('&')[0];
            // Remove query parameters if any remaining
            if (cleanId.includes('?')) cleanId = cleanId.split('?')[0];

            // Lazy Load: Store Config
            if (!this.wallYoutubeConfigs) this.wallYoutubeConfigs = [];

            // --- POSTER (PREVIEW) - DESKTOP ONLY ---
            // On mobile, loadVideos handles the thumbnail mesh to save memory/logic
            const isMobile = Section.isMobileDevice();
            let posterMesh = null;
            let posterMat = null;

            if (!isMobile) {
                // Load Thumbnail to show while video loads (avoids black screen)
                const thumbUrl = `https://img.youtube.com/vi/${cleanId}/maxresdefault.jpg`;
                const posterGeo = new THREE.PlaneGeometry(4.4, 4.4);
                posterMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 });
                posterMesh = new THREE.Mesh(posterGeo, posterMat);
                posterMesh.position.set(0, 0, 0.05); // In front of everything
                videoGroup.add(posterMesh);

                // Load texture asynchronously
                new THREE.TextureLoader().load(thumbUrl, (tex) => {
                    if (posterMat) {
                        posterMat.map = tex;
                        posterMat.needsUpdate = true;
                    }
                });
            }

            // Store Poster in Config for Animation
            this.wallYoutubeConfigs.push({
                group: videoGroup,
                id: cleanId,
                screen: screen, // Store reference for Hole Punching
                poster: posterMesh,
                posterMat: posterMat
            });

            // --- WEBGL WHITE BORDER ---
            // Replaces the CSS border. Solid 3D object.
            // Video is 4.4m wide. We want a border of ~0.15m on each side.
            // So Total Size = 4.4 + 0.3 = 4.7m
            const borderGeo = new THREE.PlaneGeometry(4.7, 4.7);
            const borderMat = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
            const borderMesh = new THREE.Mesh(borderGeo, borderMat);
            // Position slightly behind video/mask (Z=0)
            borderMesh.position.set(0, 0, -0.01);
            videoGroup.add(borderMesh);

            // --- WEBGL BUTTON ---
            // Replaced CSS3D with Mesh for correct Depth/Occlusion
            const btnMesh = this.createWebGLButton('AGRANDIR', cleanId);
            btnMesh.position.set(0, -2.8, 0.2); // Z=0.2 Closer to wall for better perspective
            videoGroup.add(btnMesh);

            // Create 3D Backing Frame (The Relief)
            // Must be larger than white border (4.7)
            // Let's go to 4.9
            const frameGeo = new THREE.BoxGeometry(4.9, 4.9, 0.1);

            // MOBILE OPTIMIZATION: Simple Material vs Standard
            let frameMat;
            if (isMobile) {
                frameMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
            } else {
                frameMat = new THREE.MeshStandardMaterial({
                    color: 0x111111, // Almost black
                    roughness: 0.6,
                    metalness: 0.2
                });
            }
            const frameMesh = new THREE.Mesh(frameGeo, frameMat);
            // Position BEHIND the White Border (Z=-0.01)
            // Box depth 0.1, front face at Z + 0.05.
            // We want front face at -0.02 approx.
            // So Z = -0.07.
            frameMesh.position.set(0, 0, -0.07);

            videoGroup.add(frameMesh);

        } else {
            // Fallback for local videos (if any remain)
            const video = document.createElement('video');
            // Handle both string paths and object paths
            const src = (typeof videoData === 'string') ? videoData : (videoData.path || '');

            if (!src) {
                console.warn(`[Section ${this.id}] Invalid video source:`, videoData);
                return;
            }

            video.src = src;
            video.crossOrigin = "anonymous";
            video.loop = true;
            video.muted = true;
            // video.play(); // STOP AUTO PLAY FOR LAZY LOADING

            // Store for Lazy Loading
            if (!this.wallVideos) this.wallVideos = [];
            this.wallVideos.push(video);
            // this.wallVideos.push({ video: video, screen: screen }); // Removed strictly to prevent crash iterators

            const texture = new THREE.VideoTexture(video);
            const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
            const geometry = new THREE.PlaneGeometry(4, 4);

            const vidMesh = new THREE.Mesh(geometry, material);
            vidMesh.position.z = 0.02;
            videoGroup.add(vidMesh);

            // Frame
            const frameGeo = new THREE.PlaneGeometry(4.4, 4.4);
            const frameMat = new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.1,
                side: THREE.DoubleSide
            });
            const frame = new THREE.Mesh(frameGeo, frameMat);
            // Add a border line
            const edges = new THREE.EdgesGeometry(frameGeo);
            const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.3 }));
            frame.add(line);
            videoGroup.add(frame);
        }

        // Position the Group
        // Position along the walls
        // Alternate left and right
        const isLeft = index % 2 === 0;
        const xPos = isLeft ? -this.width / 2 + 0.1 : this.width / 2 - 0.1;
        // Note: For CSS3D, we might need to offset slightly more to avoid z-fighting if it was a mesh, 
        // but here it's floating. 0.1 is fine.
        // Actually, if it's on the wall, and wall is at width/2.
        // Wall normal points inwards.

        // Positioning:
        // Start at videoStartZ (Local Z)
        // Step by 5.2m (Reduced spacing)
        const fixedStep = 5.2;
        const zPos = this.videoStartZ - (Math.floor(index / 2) * fixedStep);

        videoGroup.position.set(xPos, 0, zPos);
        videoGroup.rotation.y = isLeft ? Math.PI / 2 : -Math.PI / 2;

        // Add interaction data (though CSS3D handles click internally for iframe)
        videoGroup.userData = {
            type: 'video-plane',
            src: videoData
        };

        this.group.add(videoGroup);
    }

    disposeResources() {
        if (this.disposed) return;
        // console.log(`[Section ${this.id}] Disposing heavy resources...`);

        this.unloadVideos();

        // 1. Dispose Hub Texture
        if (this.hubTexture) {
            this.hubTexture.dispose();
            this.hubTexture = null;
        }
        if (this.hubLabel && this.hubLabel.material) {
            this.hubLabel.material.map = null;
            this.hubLabel.material.needsUpdate = true;
        }

        // 2. Dispose Ceiling Scroll (Unique per section)
        // If we disposed the texture, we need to rebuild it on restore.
        // Simplest strategy: Hide it, and if texture was disposed, we'll recreate it in restore.
        if (this.ceilingScroll) {
            if (this.ceilingScroll.material.map) this.ceilingScroll.material.map.dispose();
            this.ceilingScroll.visible = false;
        }

        // 3. Dispose Snow Effect
        if (this.snowEffect) {
            this.group.remove(this.snowEffect.getMesh());
            const mesh = this.snowEffect.getMesh();
            if (mesh.geometry) mesh.geometry.dispose();
            if (mesh.material) {
                if (mesh.material.map) mesh.material.map.dispose();
                mesh.material.dispose();
            }
            this.snowEffect = null;
        }

        // Clear Canvas Refs
        this.hubCtx = null;
        this.hubCanvas = null;

        // Clear Image Cache (Frees RAM)
        this.principalCache = null;
        this.principals = null;

        this.disposed = true;
    }

    restoreResources() {
        if (!this.disposed) return;
        // console.log(`[Section ${this.id}] Restoring heavy resources...`);

        // 1. Recreate Hub Canvas & Texture
        const isMobile = Section.isMobileDevice();
        const resolutionScale = isMobile ? 0.5 : 1.0;

        this.hubCanvas = document.createElement('canvas');
        this.hubCanvas.width = 2048 * resolutionScale;
        this.hubCanvas.height = 2868 * resolutionScale;
        this.hubCtx = this.hubCanvas.getContext('2d');

        this.hubTexture = new THREE.CanvasTexture(this.hubCanvas);

        if (this.hubLabel && this.hubLabel.material) {
            this.hubLabel.material.map = this.hubTexture;
            this.hubLabel.material.needsUpdate = true;
        }

        // 2. Redraw Content
        // this.loadVideos(); // setActive handles this
        this.drawHubContent();

        // 3. Restore Ceiling Scroll
        if (this.index === 0) {
            if (this.ceilingScroll) this.group.remove(this.ceilingScroll);
            this.createCeilingScrollHint();
        }

        // 4. Restore Snow
        if (this.id === 'noel' && !this.snowEffect && !isMobile) {
            this.snowEffect = new SnowEffect(this.width, this.length, this.height);
            this.group.add(this.snowEffect.getMesh());
        }

        this.disposed = false;
    }


    static createParquetTexture() {
        const size = 512;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Base Wood Color (Natural Oak / Beige - Match Chevron Ref)
        const baseHue = 38;
        const baseSat = 45; // Natural Wood Saturation
        const baseLit = 70; // Light Standard Oak
        ctx.fillStyle = `hsl(${baseHue}, ${baseSat}%, ${baseLit}%)`;
        ctx.fillRect(0, 0, size, size);

        // 1. Draw Grain (Subtle Gray-Brown natural lines)
        ctx.globalCompositeOperation = 'multiply';

        for (let i = 0; i < 800; i++) {
            const x = Math.random() * size;
            const width = Math.random() * 4 + 2;
            const opacity = 0.08;

            ctx.beginPath();
            const curve = (Math.random() - 0.5) * 50;

            ctx.moveTo(x, 0);
            ctx.bezierCurveTo(x + curve, size / 3, x - curve, size * 2 / 3, x, size);

            ctx.strokeStyle = `hsla(${baseHue}, 35%, 50%, ${opacity})`; // Less saturated grain
            ctx.lineWidth = width;
            ctx.stroke();
        }

        // 3. Noise / Texture (Speckles)
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = `hsla(${baseHue}, ${baseSat}%, ${baseLit - 40}%, 0.05)`;
        for (let n = 0; n < 5000; n++) {
            ctx.fillRect(Math.random() * size, Math.random() * size, 1, 1);
        }

        // 4. Subtle Plank Seams (Optional - Just large subtle vertical variations for structure)
        // Helps avoid the "infinite sheet" look
        ctx.globalCompositeOperation = 'overlay';
        ctx.fillStyle = 'rgba(0,0,0,0.02)';
        ctx.fillRect(0, 0, size / 2, size); // Left half subtly darker? Or random stripes

        ctx.globalCompositeOperation = 'source-over';

        const tex = new THREE.CanvasTexture(canvas);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.anisotropy = 4;
        return tex;
    }
}
